import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// All server-side timestamps are stored / emitted in UTC. The firm operates
// out of India, so every visible date/time should render in IST regardless
// of the viewer's browser timezone (e.g. travelling staff abroad).
const DISPLAY_TZ = "Asia/Kolkata";

// Centralised parser:
//   - Date-only strings ("2026-05-14") render as wall-time in IST so the
//     calendar buckets line up.
//   - Datetime strings with an explicit timezone marker (Z, +hh:mm, -hh:mm)
//     parse honestly and convert to IST.
//   - Datetime strings *without* a marker are treated as UTC. Laravel's
//     Eloquent `serializeDate()` historically emits "Y-m-d H:i:s" (no Z),
//     and the rows are stored as UTC, so without this assumption the time
//     would render as if it were already IST and be 5h30m off.
function inTz(input) {
  if (input === null || input === undefined || input === "") return dayjs.invalid();
  const s = String(input);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dayjs.tz(s, DISPLAY_TZ);
  const hasTzMarker = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  if (hasTzMarker) return dayjs(s).tz(DISPLAY_TZ);
  return dayjs.utc(s).tz(DISPLAY_TZ);
}

// Today's date in IST. Use this whenever the UI compares against "today" so
// the comparison is correct even for staff travelling abroad (the firm runs
// on Indian time).
export const nowIst = () => dayjs().tz(DISPLAY_TZ);
export const todayKeyIst = () => nowIst().format("YYYY-MM-DD");

// Whole minutes elapsed from a stored timestamp until now (never negative).
// tz-aware via the same parser as the formatters, so an open check-in slot
// can show a live "worked so far" figure that agrees with the server.
export const minutesSince = (ts) => {
  const d = inTz(ts);
  return d.isValid() ? Math.max(0, nowIst().diff(d, "minute")) : 0;
};

// ISO (YYYY-MM-DD) date math in IST, for <input type="date"> values and the
// subscription expiry presets. `base` defaults to today; pass an existing
// period_end to extend relative to it.
const isoFrom = (base) => (base ? dayjs.tz(String(base).slice(0, 10), DISPLAY_TZ) : nowIst());
export const isoPlusDays = (n, base) => isoFrom(base).add(n, "day").format("YYYY-MM-DD");
export const isoPlusMonths = (n, base) => isoFrom(base).add(n, "month").format("YYYY-MM-DD");
export const isoPlusYears = (n, base) => isoFrom(base).add(n, "year").format("YYYY-MM-DD");

export const formatINR = (n) => {
  if (n == null) return "—";
  const x = Math.round(Number(n));
  if (Number.isNaN(x)) return "—";
  if (x >= 10000000) return `₹${(x / 10000000).toFixed(2)} Cr`;
  if (x >= 100000) return `₹${(x / 100000).toFixed(2)} L`;
  if (x >= 1000) return `₹${(x / 1000).toFixed(1)}K`;
  return `₹${x}`;
};

export const formatDate = (s) => {
  if (!s) return "—";
  const d = inTz(s);
  return d.isValid() ? d.format("DD MMM YYYY") : "—";
};

export const formatDateShort = (s) => {
  if (!s) return "—";
  const d = inTz(s);
  return d.isValid() ? d.format("DD MMM") : "—";
};

export const formatDateTime = (s) => {
  if (!s) return "—";
  const d = inTz(s);
  return d.isValid() ? d.format("DD MMM YYYY · h:mm A") : "—";
};

export const fromNow = (s) => {
  if (!s) return "—";
  const d = inTz(s);
  if (!d.isValid()) return "—";
  const diffMs = Date.now() - d.valueOf();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.format("DD MMM");
};

export const daysFromToday = (s) => {
  if (!s) return null;
  const d = inTz(s).startOf("day");
  const today = dayjs().tz(DISPLAY_TZ).startOf("day");
  return d.diff(today, "day");
};

export const initials = (name) => {
  if (!name) return "??";
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export const ticketNumber = (id) => `#${String(id).padStart(4, "0")}`;

// Scale a byte count to a readable unit: KB under 1 MB, MB under 1 GB, else GB.
// So a few small files read as "3.2 MB" instead of rounding to "0 GB".
export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 ** 3)).toFixed(2)} GB`;
};

// Serial number for a row in a paginated list. Uses Laravel's `from` (1-based start
// of the current page) when available; falls back to a local index for client-side lists.
export const serialNumber = (meta, index) => {
  const from = meta?.from;
  if (typeof from === "number") return from + index;
  return index + 1;
};

// Compares completion_date / today vs due_date and returns a human-readable verdict.
// Returns { label, tone } where tone is 'success' | 'warning' | 'danger' | 'neutral' | 'info'.
export const analyzeTaskTiming = (task) => {
  if (!task || !task.due_date) return { label: "—", tone: "neutral" };

  const dueKey = String(task.due_date).slice(0, 10);
  const due = inTz(dueKey).startOf("day");
  if (!due.isValid()) return { label: "—", tone: "neutral" };

  const status = task.status;
  const completedKey = task.completion_date
    ? String(task.completion_date).slice(0, 10)
    : null;

  if (status === "completed" && completedKey) {
    const completed = inTz(completedKey).startOf("day");
    const diff = completed.diff(due, "day");
    if (diff < 0) return { label: `Completed ${Math.abs(diff)}d early`, tone: "success" };
    if (diff === 0) return { label: "Completed on time", tone: "success" };
    return { label: `Completed ${diff}d late`, tone: "warning" };
  }

  if (status === "completed") {
    return { label: "Completed", tone: "success" };
  }

  if (status === "cancelled") return { label: "Cancelled", tone: "neutral" };

  const today = dayjs().tz(DISPLAY_TZ).startOf("day");
  const diff = due.diff(today, "day");
  if (diff < 0) return { label: `Overdue by ${Math.abs(diff)}d`, tone: "danger" };
  if (diff === 0) return { label: "Due today", tone: "warning" };
  if (diff <= 7) return { label: `Due in ${diff}d`, tone: "info" };
  return { label: `Due in ${diff}d`, tone: "neutral" };
};

// Exact rupee amount with Indian grouping (₹1,23,456.00). Use for receipts
// and bill details where the precise value matters. KPI cards / overview
// chips should prefer the compact `formatINR` above.
const INR_EXACT = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});
export const formatINRExact = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (Number.isNaN(n)) return "—";
  return INR_EXACT.format(n);
};
